import { ApiClient, type ApiClient as ApiClientType } from '@cef-ai/wallet-api-client';
import {
  IdentityImpl,
  SoftAuthenticator,
  browserCredentialStore,
  WebAuthnCeremonyAdapter,
  createSecretboxWrapper,
  type CeremonyAdapter,
  type Identity,
  type KeystoreEncryptor,
  type RootUnwrapper,
} from '@cef-ai/wallet-identity';
import { spaKeystoreEncryptor } from '~/wallet/export/keystoreEncryptor';

export interface CreateIdentityOptions {
  /** Base URL of the wallet-api backend, e.g. `https://wallet-api.example.com`. */
  apiBaseUrl: string;
  /**
   * Optional ceremony adapter override. Defaults to a real
   * `WebAuthnCeremonyAdapter`. Tests / dev environments may inject a
   * `SoftAuthenticator` or other CeremonyAdapter implementation.
   */
  ceremony?: CeremonyAdapter;
  /**
   * Keystore encoder injected into IdentityImpl for wallet export. Defaults to
   * the SPA's `spaKeystoreEncryptor`; tests may override.
   */
  keystoreEncryptor?: KeystoreEncryptor;
  /**
   * Opens a non-origin credential's root envelope during `login()`. Defaults to
   * the package's `createSecretboxWrapper()` (XSalsa20-Poly1305, the format
   * wallet-api stores); tests may override.
   */
  rootUnwrapper?: RootUnwrapper;
}

export interface IdentityDeps {
  apiClient: ApiClientType;
  identity: Identity;
}

/**
 * Construct the wallet SPA's identity dependencies. Returns the two singletons
 * the SPA needs at boot: a configured `ApiClient` and an `Identity` instance
 * that shares the JWT cache with the api-client.
 *
 * The wallet SPA composes this with stores (Cycle 5e) and popup-side bridge
 * handlers (Cycle 6c) at boot.
 */
export function createIdentity(opts: CreateIdentityOptions): IdentityDeps {
  // Test-only env override: when `VITE_USE_SOFT_AUTHENTICATOR=true`, swap the
  // real WebAuthn ceremony adapter for the deterministic in-memory
  // `SoftAuthenticator`. Used by Playwright e2e specs — not because Chromium's
  // virtual authenticator lacks PRF (`hasPrf` works), but because CDP cannot
  // carry PRF state across the popup boundary: no hmac-secret field on
  // `Credential`, and a per-target WebAuthn domain. See README
  // "End-to-end testing".
  //
  // Hard-guard against accidental production exposure: a soft authenticator in
  // a production build would replace real passkey authentication with
  // in-process keys that provide zero security. Throwing at module-load
  // construction time fails the SPA on first render so the regression is
  // impossible to ship silently. Vite injects `import.meta.env.PROD` as a
  // boolean true only in production builds (`vite build` without --mode
  // development), so this guard is dead code in dev/test.
  const useSoft = (import.meta as any).env?.VITE_USE_SOFT_AUTHENTICATOR === 'true';
  if (useSoft && (import.meta as any).env?.PROD === true) {
    throw new Error(
      'VITE_USE_SOFT_AUTHENTICATOR=true is forbidden in production builds. ' +
        'This flag swaps real WebAuthn for in-memory deterministic keys; ' +
        'shipping it would expose every user to trivial impersonation.',
    );
  }
  // Late-bound identity reference: the ApiClient needs a `getAuthToken` callback
  // that resolves the current JWT, but identity needs the ApiClient to exist.
  // Resolve the cycle by closure.
  //
  // `getJwt()`, NOT a bare read of the cached token. This is the contract
  // docs/delegation-token-lifecycle.md §2 already describes ("it needs a live
  // JWT, which it obtains via `identity.getJwt()`"): getJwt refreshes when the
  // cached token is within JWT_REFRESH_SLACK_MS of expiry — silently off the
  // HttpOnly refresh cookie for a bearer-active session, de-duped so concurrent
  // callers cannot replay a rotated refresh token and burn the family.
  //
  // This used to read `identityRef?.getCachedJwtForApiClient()`, which returns
  // the cached token with no expiry check and no refresh. The JWT TTL (~20m) is
  // far shorter than a tab's life, so every authRequired call from an idle tab
  // sent a long-dead token and wallet-api answered `token-expired` — forever,
  // because nothing on this path ever refreshed. It surfaced in ROC as a raw
  // "token-expired: token has invalid claims: token is expired" under the
  // "Generate access token" button, and left `getJwt()` dead code.
  //
  // Pinned by src/bootstrap/__tests__/createIdentity.authToken.test.ts. Safe
  // against recursion: no endpoint reachable from getJwt() is itself
  // `authRequired` — session.refresh is cookie-authorized and passkey.* are
  // challenge-authorized — so none of them re-enters this callback.
  let identityRef: IdentityImpl | null = null;
  const apiClient = new ApiClient({
    baseUrl: opts.apiBaseUrl,
    getAuthToken: () => identityRef?.getJwt() ?? null,
  });
  // When the soft authenticator is active (e2e only), seed it deterministically
  // so the addresses it derives match the hardcoded stubs in
  // tests-e2e/auth-flows.spec.ts. The seed string is not a secret — it only
  // matters that it is stable across runs. `crossCheckAddresses` then exercises
  // a real equality check rather than the empty-object short-circuit. The seed
  // never reaches a production build because the `useSoft` branch is guarded
  // above. See packages/identity/src/SoftAuthenticator.ts for the seed contract.
  //
  // `fallbackToLastRegistered: true` is required for the e2e post-logout
  // re-login flow: after logout the wallet's vault has dropped the
  // credentialId, so IdentityImpl.login() will call the ceremony with
  // `credentialId: undefined`. The SoftAuthenticator's default now throws in
  // that case (defence in depth against the silent-fallback foot-gun the
  // post-cycle-8 review flagged); the opt-in restores the e2e-friendly
  // behaviour. Production never reaches this branch because `useSoft` is
  // hard-guarded above.
  //
  // `store: browserCredentialStore()` shares the credentials across every
  // same-origin window, which is what a real platform authenticator does and
  // what the in-memory Map never did. Without it a credential minted in the
  // registration popup is invisible to the iframe that must then re-derive
  // from it, so `hasKeys` is false after every e2e registration and the whole
  // registration path is untestable (CEF-AI/wallet#91). Playwright's per-test
  // BrowserContext is what keeps one test's credentials out of the next.
  const defaultCeremony: CeremonyAdapter = useSoft
    ? new SoftAuthenticator({
        seed: 'cere-wallet-e2e-soft-authenticator-seed-v1',
        fallbackToLastRegistered: true,
        store: browserCredentialStore(),
      })
    : new WebAuthnCeremonyAdapter();
  const ceremony: CeremonyAdapter = opts.ceremony ?? defaultCeremony;
  identityRef = new IdentityImpl({
    apiClient,
    ceremony,
    keystoreEncryptor: opts.keystoreEncryptor ?? spaKeystoreEncryptor,
    rootUnwrapper: opts.rootUnwrapper ?? createSecretboxWrapper(),
  });
  return { apiClient, identity: identityRef };
}
